import { escapeHTML } from '../../utils/escape.js';
import { formatPrice, formatFechaCorta, formatFechaDia } from '../../utils/formatters.js';

/**
 * Staff "Gift cards" section (openspec/changes/staff-giftcards/spec.md B1–B3):
 * Emitir (form → image) and Canjear (vigentes, filter by "para", confirm).
 * Pure render; `js/giftcards.js` owns state and listeners. While a mode is
 * open its form is painted once (typed text survives list and notice
 * repaints); switching mode repaints the form.
 */

const SEGMENTO = 'px-5 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const INPUT = 'w-full px-4 py-3 rounded-xl border border-border-subtle bg-base text-text-primary text-base';
const LABEL = 'text-[11px] font-black uppercase tracking-widest text-text-muted';
const BOTON_PRIMARIO = 'px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg disabled:opacity-60';
const BOTON_SECUNDARIO = 'px-4 py-2.5 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-primary hover:text-brand transition-colors disabled:opacity-60';

/** @param {{ modo: 'emitir'|'canjear' }} props */
export function StaffGiftcardsShell({ modo }) {
    const seg = (m, label) => {
        const activo = m === modo;
        return `<button type="button" data-action="giftcards-modo" data-modo="${m}" aria-pressed="${activo}"
            class="${SEGMENTO} ${activo ? 'bg-brand text-white shadow-lg' : 'bg-surface border border-border-subtle text-text-muted hover:text-brand'}">${label}</button>`;
    };
    return `
        <section data-giftcards-staff class="max-w-3xl">
            <header class="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary">Gift cards</h2>
                    <p class="mt-1 text-sm text-text-muted">Emití una para regalar o canjeá una que traen al mostrador.</p>
                </div>
                <div role="group" aria-label="Qué querés hacer" class="flex gap-2">${seg('emitir', 'Emitir')}${seg('canjear', 'Canjear')}</div>
            </header>
            <div id="giftcards-staff-contenido"></div>
        </section>
    `;
}

/**
 * @param {{ montosRapidos: number[], medios: Array<{ value: string, label: string }> }} props
 */
export function EmitirForm({ montosRapidos, medios }) {
    const rapidos = montosRapidos
        .map(
            (m) =>
                `<button type="button" data-action="giftcard-monto-rapido" data-monto="${m}" class="px-3 py-2 rounded-lg border border-border-subtle text-sm font-bold tabular-nums text-text-primary hover:border-brand hover:text-brand transition-colors">${m / 1000}</button>`,
        )
        .join('');
    const radios = medios
        .map(
            (m, i) => `
            <label class="flex items-center gap-2 px-4 py-3 rounded-xl border border-border-subtle cursor-pointer has-[:checked]:border-brand has-[:checked]:text-brand text-sm font-bold text-text-primary">
                <input type="radio" name="medio" value="${m.value}" ${i === 0 ? 'checked' : ''} class="accent-[var(--brand)]">${m.label}
            </label>`,
        )
        .join('');
    return `
        <div data-giftcards-emitir class="grid gap-6">
            <form data-action-form="giftcard-emitir" novalidate class="bg-surface border border-border-subtle rounded-2xl p-5 sm:p-6 flex flex-col gap-5">
                <div class="flex flex-col gap-2">
                    <label for="giftcard-monto" class="${LABEL}">Monto</label>
                    <div class="flex items-center gap-3">
                        <span class="text-2xl font-black text-text-muted">$</span>
                        <input id="giftcard-monto" name="monto" inputmode="numeric" autocomplete="off" placeholder="30000" class="${INPUT} text-2xl font-black tabular-nums max-w-[14rem]">
                    </div>
                    <div class="flex flex-wrap gap-2" aria-label="Montos rápidos, en miles">${rapidos}</div>
                    <p class="text-[11px] text-text-muted">Desde $15.000. Los botones están en miles.</p>
                </div>
                <fieldset class="flex flex-col gap-2">
                    <legend class="${LABEL} mb-2">Cómo pagó</legend>
                    <div class="flex flex-wrap gap-2">${radios}</div>
                </fieldset>
                <div class="grid gap-5 sm:grid-cols-2">
                    <label class="flex flex-col gap-2">
                        <span class="${LABEL}">Para</span>
                        <input name="para" autocomplete="off" placeholder="Lucía" class="${INPUT}">
                    </label>
                    <label class="flex flex-col gap-2">
                        <span class="${LABEL}">De parte de <span class="normal-case tracking-normal font-bold">(opcional)</span></span>
                        <input name="de_parte" autocomplete="off" placeholder="Juan y Sofi" class="${INPUT}">
                    </label>
                </div>
                <label class="flex flex-col gap-2">
                    <span class="${LABEL}">Mensaje <span class="normal-case tracking-normal font-bold">(opcional, hasta 200)</span></span>
                    <textarea name="mensaje" rows="3" placeholder="¡Feliz cumple!" class="${INPUT} leading-relaxed resize-y"></textarea>
                </label>
                <div data-giftcards-aviso></div>
                <button type="submit" data-giftcards-emitir-boton class="self-start ${BOTON_PRIMARIO}">
                    <i class="fa-solid fa-gift mr-2" aria-hidden="true"></i>Emitir gift card
                </button>
            </form>
            <div data-giftcard-resultado hidden></div>
        </div>
    `;
}

/**
 * A card's image panel: the canvas it is drawn on, a text summary, and an
 * actions slot (`GiftcardAcciones`) repainted as the drawing progresses. Used
 * after issuing (staff) and to redraw any card (admin).
 * @param {{ imagen: { para: string, monto: number, validaHasta: string }, titulo?: string }} props
 */
export function GiftcardResultado({ imagen, titulo = 'Gift card emitida' }) {
    return `
        <div class="bg-surface border border-border-subtle rounded-2xl p-5 sm:p-6 grid gap-6 sm:grid-cols-[minmax(0,20rem)_1fr] items-start">
            <canvas data-giftcard-canvas width="1080" height="1350" role="img"
                aria-label="Gift card para ${escapeHTML(imagen.para)} por ${escapeHTML(formatPrice(imagen.monto))}"
                class="w-full h-auto rounded-xl shadow-lg bg-[#14532d]"></canvas>
            <div class="flex flex-col gap-4">
                <div role="status">
                    <p class="text-[11px] font-black uppercase tracking-widest text-brand">${escapeHTML(titulo)}</p>
                    <p class="mt-1 text-xl font-black text-text-primary break-words">Para ${escapeHTML(imagen.para)}</p>
                    <p class="text-3xl font-black tabular-nums text-text-primary">${escapeHTML(formatPrice(imagen.monto))}</p>
                    <p class="mt-1 text-sm text-text-muted">Válida hasta el ${escapeHTML(imagen.validaHasta)}</p>
                </div>
                <div data-giftcard-acciones></div>
            </div>
        </div>
    `;
}

/**
 * Download / share / print, disabled until the image is drawn; a failed
 * drawing says so and offers a retry.
 * Copy (the image to the clipboard, to paste in WhatsApp with Ctrl+V) only
 * where the browser can copy images.
 * @param {{ estado: 'dibujando'|'lista'|'error', puedeCompartir: boolean, puedeCopiar?: boolean, cierre: 'nueva'|'cerrar', aviso?: { tipo: 'ok'|'error', texto: string } | null }} props
 */
export function GiftcardAcciones({ estado, puedeCompartir, puedeCopiar = false, cierre, aviso = null }) {
    const off = estado === 'lista' ? '' : 'disabled';
    const estadoTexto =
        estado === 'dibujando'
            ? '<p class="text-xs font-bold text-text-muted" role="status"><i class="fa-solid fa-circle-notch fa-spin mr-2" aria-hidden="true"></i>Preparando la imagen...</p>'
            : estado === 'error'
              ? `<div class="flex flex-wrap items-center gap-3" role="alert"><p class="text-sm font-bold text-red-800 dark:text-red-200">No se pudo generar la imagen.</p><button type="button" data-action="giftcard-imagen-reintentar" class="${BOTON_SECUNDARIO}">Reintentar</button></div>`
              : '';
    const cierreBoton =
        cierre === 'nueva'
            ? '<button type="button" data-action="giftcard-nueva" class="self-start text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand underline underline-offset-4">Emitir otra</button>'
            : '<button type="button" data-action="giftcard-visor-cerrar" class="self-start text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand underline underline-offset-4">Cerrar</button>';
    return `
        <div class="flex flex-col gap-4">
            ${estadoTexto}
            <div class="flex flex-wrap gap-2">
                <button type="button" data-action="giftcard-descargar" class="${BOTON_PRIMARIO}" ${off}><i class="fa-solid fa-download mr-2" aria-hidden="true"></i>Descargar imagen</button>
                ${puedeCopiar ? `<button type="button" data-action="giftcard-copiar" class="${BOTON_SECUNDARIO}" ${off}><i class="fa-solid fa-copy mr-2" aria-hidden="true"></i>Copiar imagen</button>` : ''}
                ${puedeCompartir ? `<button type="button" data-action="giftcard-compartir" class="${BOTON_SECUNDARIO}" ${off}><i class="fa-solid fa-share-nodes mr-2" aria-hidden="true"></i>Compartir</button>` : ''}
                <button type="button" data-action="giftcard-imprimir" class="${BOTON_SECUNDARIO}" ${off}><i class="fa-solid fa-print mr-2" aria-hidden="true"></i>Imprimir</button>
            </div>
            ${aviso ? `<p class="text-sm font-bold ${aviso.tipo === 'ok' ? 'text-emerald-800 dark:text-emerald-200' : 'text-red-800 dark:text-red-200'}" role="${aviso.tipo === 'ok' ? 'status' : 'alert'}">${escapeHTML(aviso.texto)}</p>` : ''}
            ${cierreBoton}
        </div>
    `;
}

export function CanjearShell() {
    return `
        <div data-giftcards-canjear class="flex flex-col gap-5">
            <label class="flex flex-col gap-2">
                <span class="${LABEL}">Buscar por nombre</span>
                <input data-giftcards-buscar type="search" autocomplete="off" placeholder="¿Para quién es?" class="${INPUT}">
            </label>
            <p class="text-xs text-text-muted">Antes de canjear, pedí ver la imagen de la gift card en el celular.</p>
            <div data-giftcards-aviso></div>
            <div data-giftcards-lista></div>
        </div>
    `;
}

/**
 * @param {{
 *   uiState: { status: 'loading'|'error'|'ready', mensaje?: string },
 *   items: Array<{ id: string, para: string, de_parte: string | null, monto: number, emitida_at: string, valida_hasta: string }>,
 *   hayFiltro: boolean,
 *   confirmando: string | null,
 *   canjeando: string | null,
 * }} props
 */
export function CanjearLista({ uiState, items, hayFiltro, confirmando, canjeando }) {
    if (uiState.status === 'loading') {
        return `<div class="py-12 text-center" role="status" aria-live="polite"><i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand" aria-hidden="true"></i><p class="mt-3 text-xs font-black uppercase tracking-widest text-text-muted">Cargando gift cards...</p></div>`;
    }
    if (uiState.status === 'error') {
        return `<div class="py-12 text-center" role="alert"><p class="text-sm font-bold text-text-primary mb-5">${escapeHTML(uiState.mensaje ?? 'No se pudieron cargar las gift cards.')}</p><button type="button" data-action="giftcards-reintentar" class="${BOTON_PRIMARIO}">Reintentar</button></div>`;
    }
    if (items.length === 0) {
        return `<p class="py-12 text-center text-sm text-text-muted">${hayFiltro ? 'Ninguna gift card vigente con ese nombre.' : 'No hay gift cards vigentes.'}</p>`;
    }
    const filas = items
        .map((g) => {
            const id = escapeHTML(g.id);
            const ocupado = canjeando === g.id;
            const confirma = confirmando === g.id;
            const confirmacion = confirma
                ? `<div class="basis-full flex flex-wrap items-center justify-between gap-3 rounded-xl bg-black/5 dark:bg-white/10 px-4 py-3">
                    <p class="text-sm font-bold text-text-primary">¿Canjear ${escapeHTML(formatPrice(g.monto))} para ${escapeHTML(g.para)}?</p>
                    <div class="flex gap-2">
                        <button type="button" data-action="giftcard-canjear-cancelar" class="${BOTON_SECUNDARIO}" ${ocupado ? 'disabled' : ''}>Cancelar</button>
                        <button type="button" data-action="giftcard-canjear-confirmar" data-id="${id}" class="${BOTON_PRIMARIO}" ${ocupado ? 'disabled' : ''}>${ocupado ? 'Canjeando...' : 'Sí, canjear'}</button>
                    </div>
                </div>`
                : '';
            return `
                <article data-giftcard-id="${id}" class="flex flex-wrap items-center justify-between gap-4 py-4 border-b border-border-subtle last:border-b-0">
                    <div class="min-w-0">
                        <p class="text-lg font-black text-text-primary break-words">${escapeHTML(g.para)}</p>
                        ${g.de_parte ? `<p class="text-sm text-text-muted break-words">De parte de ${escapeHTML(g.de_parte)}</p>` : ''}
                        <p class="mt-1 text-xs text-text-muted">Emitida el ${escapeHTML(formatFechaCorta(g.emitida_at))} · Válida hasta el ${escapeHTML(formatFechaDia(g.valida_hasta))}</p>
                    </div>
                    <div class="flex items-center gap-4">
                        <p class="text-2xl font-black tabular-nums text-text-primary">${escapeHTML(formatPrice(g.monto))}</p>
                        ${confirma ? '' : `<button type="button" data-action="giftcard-canjear" data-id="${id}" class="${BOTON_SECUNDARIO}">Canjear</button>`}
                    </div>
                    ${confirmacion}
                </article>`;
        })
        .join('');
    return `<div class="bg-surface border border-border-subtle rounded-2xl px-5">${filas}</div>`;
}

/** @param {{ tipo: 'ok'|'error', texto: string } | null} aviso */
export function GiftcardsAviso(aviso) {
    if (!aviso) return '';
    const ok = aviso.tipo === 'ok';
    const clases = ok
        ? 'border-emerald-600/30 bg-emerald-600/10 text-emerald-800 dark:text-emerald-200'
        : 'border-red-600/30 bg-red-600/10 text-red-800 dark:text-red-200';
    return `<div role="${ok ? 'status' : 'alert'}" class="flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-bold ${clases}">
        <i class="fa-solid ${ok ? 'fa-circle-check' : 'fa-circle-exclamation'}" aria-hidden="true"></i><span>${escapeHTML(aviso.texto)}</span></div>`;
}
